<template>
    <div ref="annotation" :id="`annotation-${pageNumber}`" class="page" @click="leftClick($event, pageNumber)">
        <slot></slot>
        <div class="annotations" :class="{active: isAnnotating}" :style="annotationStyle" @click="hideContextMenu()"
             @contextmenu.prevent.stop="showContextMenu($event)">
            <div class="wrapper">
                <div class="textLayer"></div>
                <vue-draggable-resizable
                    v-if="signatureBlock.page_num == pageNumber && isAddingSignature"
                    v-for="(signatureBlock, index) in signatures"
                    :key="signatureBlock.id"
                    :x="signatureBlock.x"
                    :y="signatureBlock.y"
                    :w="signatureBlock.width > 0 ? signatureBlock.width : 240"
                    :h="signatureBlock.height"
                    :ref="'draggable' + signatureBlock.id"
                    :draggable="signatureBlock.active"
                    :resizable="false"
                    :parent="true">
                    <div class="maskActive-Signature d-flex align-items-center justify-content-start p-2">
                        <p class="mb-0 mr-2">
                            <i class="fas fa-pen-nib fa-2x text-white"></i>
                        </p>
                        <p class="mask-label mb-0">
                            <strong>Signature</strong>
                            <br/>
                            {{ getName(signatureBlock) }}
                        </p>
                        <button class="mask-label mask-button" @click.stop="deleteSignature(index)">
                            <i class="fas fa-times-circle"></i>
                        </button>
                    </div>
                </vue-draggable-resizable>
                <vue-draggable-resizable
                    v-if="annotation.page_num == pageNumber && isAnnotating" v-for="(annotation, index) in annotations"
                    :key="annotation.client_id"
                    :ref="`draggable-${annotation.client_id}`"
                    :w="annotation.width"
                    :h="annotation.height"
                    :x="annotation.x"
                    :y="annotation.y"
                    @dragging="(x, y) => {dragged(x, y, annotation)}"
                    @resizing="(x,y, w, h) => {resized(x, y, w, h, annotation)}"
                    @activated="$set(annotation, 'activated', true)"
                    @deactivated="$set(annotation, 'activated', false)"
                    :draggable="true"
                    :resizable="annotation.type != 'SIGN'"
                    :parent="true">
                    <div class="maskActive-Annotation">
                        <div v-if="annotation.type ==='TEXT'" style="height:100%; width:100%;">
                            <div style="height:100%; width:100%;" class="mask-label ml-1" v-if="!annotation.editted"
                                 @click.stop="annotation.editted = true" :style="annotation.styles">
                                {{ annotation.text ? annotation.text : 'Click here to update' }}
                            </div>

                            <textarea
                                style="height:100%;width:100%;"
                                :style="annotation.styles"
                                type="text"
                                class="transparent-field"
                                placeholder="Update Label here"
                                v-model="annotation.text"
                                v-if="annotation.editted"
                                @blur="saveText(annotation)"
                            ></textarea>
                            <TextStyler :styles="annotation.styleOptions" :isVisible="annotation.activated"
                                        @removeItem="removeAnnotation(index)"
                                        @styleOptions="styleOptions($event, annotation)"/>
                        </div>

                        <button v-if="annotation.type ==='IMAGE'" class="mask-label mask-button"
                                @click="removeAnnotation(index)">
                            <i class="fas fa-times-circle"></i>
                        </button>

                        <img
                            :src="annotation.src"
                            class="disselect img-fluid w-100 h-100"
                            v-if="annotation.type ==='IMAGE'"
                        />
                    </div>
                    <div v-if="annotation.type ==='SIGN'"
                         class="maskActive-Signature d-flex align-items-center justify-content-start p-2">
                        <p class="mb-0 mr-2">
                            <i class="fas fa-pen-nib fa-2x text-white"></i>
                        </p>
                        <p class="mask-label mb-0">
                            <strong>Signature</strong>
                            <br/>
                            {{ getName(annotation) }}
                        </p>
                        <button class="mask-label mask-button" @click.stop="removeAnnotation(index)">
                            <i class="fas fa-times-circle"></i>
                        </button>
                    </div>
                </vue-draggable-resizable>
                <context-menu v-bind="{pageNumber, isAnnotating, isAddingSignature}"
                              @add-signature="addSignature"></context-menu>
            </div>
        </div>
    </div>
</template>

<script>
import {PIXEL_RATIO} from './utils/constants';
import ContextMenu from './PDFContextMenu';
import {ulid} from "ulid";
import TextStyler from './TextStyler';

export default {
    name: 'PDFAnnotation',
    components: {
        ContextMenu,
        TextStyler
    },
    props: {
        page: {
            type: Object,
            required: true,
        },
        pdfid: {
            required: false,
            default: false,
        },
        scale: {
            type: Number,
            required: true,
        },
        optimalScale: {
            type: Number,
            required: true,
        },
        isPageFocused: {
            type: Boolean,
            default: false,
        },
        isAddingSignature: {
            default: false,
        },
        isAnnotating: {
            type: Boolean
        },
        annotations: {
            default: false,
        },
        isElementFocused: {
            type: Boolean,
            default: false,
        },
        isApplicantSet: {
            type: Object,
        },
        contacts: {
            type: Array
        },
        applicants: {
            type: Array,
        },
        fit: {
            type: String,
        },
        documentData: {
            type: Object,
        },
        headers: {
            type: Object,
            required: true
        },
        workspace: {
            required: false
        },
        currentPage: {
            required: true
        },
        copiedAnnotation: {
            required: false
        },
        leftClickedArea: {
            required: false
        },
    },
    data() {
        return {
            signatures: [],
            dragTimeout: null,
        }
    },
    computed: {
        pageNumber() {
            return `${this.page.pageNumber}${this.pdfid}`;
        },

        annotationStyle() {
            const {viewport} = this;
            const [pixelWidth, pixelHeight] = [(viewport.width / viewport.scale) * 2, (viewport.height / viewport.scale) * 2]

            if (this.fit == 'fixed') return `width: ${pixelWidth}px!important; height: ${pixelHeight}px!important; max-width: none!important; `;
            return `width: ${pixelWidth}px; height: ${pixelHeight}px; `;
        },
    },
    watch: {
        isElementFocused(isElementFocused) {
            if (isElementFocused) this.focusPage();
        },
        isApplicantSet(annotate) {
            if (annotate.page_num != this.pageNumber) return;
            this.insertSignature(annotate);
        }
    },
    created() {
        this.viewport = this.page.getViewport(this.optimalScale);
        window.addEventListener('keydown', this.copyOrPasteAnnotation);
    },
    destroyed() {
        window.removeEventListener('keydown', this.copyOrPasteAnnotation);
    },
    mounted() {
        if (this.documentData.signatures.length) {
            this.displaySignatures(this.documentData.signatures);
        }
    },
    updated() {
    },
    activated() {
    },
    methods: {
        getName(item) {
            let contact = '';
            if (item.user_id != undefined) {
                contact = this.contacts.find(appl => {
                    return appl.user_id === item.user_id
                })
            } else {
                contact = this.contacts.find(appl => {
                    return appl.id === item.applicant_id
                })
            }

            if (_.get(contact, 'first_name') && _.get(contact, 'last_name')) {
                return `${contact.first_name} ${contact.last_name}`;
            }
        },

        dragged(x, y, annotation) {
            annotation.x = x;
            annotation.y = y;
        },

        resized(x, y, w, h, annotation) {
            annotation.x = x;
            annotation.y = y;
            annotation.width = w;
            annotation.height = h;
        },

        leftClick($event, pageNumber) {
            let leftClickedArea = {event: $event, page_num: pageNumber};
            this.$bus.$emit('pdf::annotation::left::click', leftClickedArea);
        },

        copyOrPasteAnnotation(e) {
            let key = e.keyCode;
            let ctrl = e.metaKey || e.ctrlKey;
            if (ctrl != true || _.get(e, 'path.0.localName') == 'textarea') return;
            if (e.Handled) return;
            switch (key) {
                case 67:
                    let annotation = this.annotations.find(a => {
                        return a.activated == true;
                    });
                    if (!annotation) return;
                    let copy = Object.assign({}, annotation);
                    this.$delete(copy, 'id')

                    this.$bus.$emit('pdf::annotation::copy', copy);
                    this.$bus.$emit('pdf::annotation::left::click', null);
                    break;
                case 86:
                    if (this.copiedAnnotation == null) return;
                    let paste = Object.assign({}, this.copiedAnnotation);
                    paste.id = ulid();

                    paste.page_num = (this.leftClickedArea != null ? this.leftClickedArea.page_num : this.copiedAnnotation.page_num);
                    paste.x = (this.leftClickedArea != null ? this.leftClickedArea.event.offsetX : this.copiedAnnotation.x);
                    paste.y = (this.leftClickedArea != null ? this.leftClickedArea.event.offsetY : this.copiedAnnotation.y),
                        this.$bus.$emit('pdf::annotation::add', paste);
                    break;
                default:
                    return;
                    break;
            }
            e.Handled = true;
        },

        focusPage() {
            if (this.isPageFocused) return
        },

        saveText(annotation) {
            annotation.editted = false;
        },

        // Signature Methods

        addSignature() {
            this.$emit('select-applicant');
        },

        insertSignature(signature) {
            if (this.isAnnotating) {
                let object = {
                    id: ulid(),
                    page_num: signature.page_num,
                    x: (this.clickedArea != null ? this.clickedArea.offsetX : 0),
                    y: (this.clickedArea != null ? this.clickedArea.offsetY : 0),
                    type: "SIGN",
                    width: 240,
                    height: 64,
                    applicant_id: signature.id,
                    user_id: signature.user_id,
                    editted: false,
                    object_id: null,
                    new: true,
                }
                this.$bus.$emit('pdf::annotation::add', object);
            } else {
                let object = {
                    page_num: signature.page_num,
                    applicant_id: signature.id,
                    user_id: signature.user_id,
                    active: true,
                    id: ulid(),
                    x: this.clickedArea.offsetX,
                    y: this.clickedArea.offsetY,
                    width: 240,
                    height: 64,
                    new: true
                }
                this.signatures.push(object);
                this.$bus.$emit('pdf::signature::add', object);
            }
        },

        displaySignatures(signatures) {
            signatures.forEach(signature => {
                if (this.pageNumber == signature.page_num) {
                    let object = {
                        page_num: signature.page_num,
                        applicant_id: signature.applicant_id,
                        active: true,
                        id: signature.id,
                        client_id: signature.client_id,
                        x: signature.x,
                        y: signature.y,
                        width: signature.width,
                        height: signature.height,
                        new: false
                    };

                    this.signatures.push(object);
                    this.$emit('add-signature', object);
                }
            });
        },

        styleOptions(payload, annotation) {
            annotation.styleOptions = payload.styles;
            let fontWeight = payload.styles.textStyle.bold ? 900 : 400;
            let decoration = payload.styles.textStyle.underline ? 'underline' : 'none';
            let textStyle = payload.styles.textStyle.italic ? 'italic' : 'none';
            let fontSize = payload.styles.font;

            annotation.styles = `color: ${payload.styles.color}; text-decoration: ${decoration}; font-style: ${textStyle}; font-weight: ${fontWeight}; font-size: ${fontSize}pt;`
        },
        // Save and Delete methods

        async deleteSignature(index) {
            try {
                let signature = this.signatures[index];
                if (signature) {
                    signature.active = false;
                    if (!signature.new) {
                        let res = await axios.delete(
                            `/api/v2/applications/${this.$store.getters.initAppData.id}/${
                                this.documentData.type === "documents"
                                    ? "image-documents"
                                    : "image-app-documents"
                            }/${this.documentData.id}/annotations/${
                                signature._server_id ? signature._server_id : signature.id
                            }`, // _server_id is the id from the server for new planted objects
                            this.headers
                        );
                    }
                    this.signatures.splice(index, 1);
                    this.$bus.$emit('pdf::signature::remove', index);
                }
            } catch (err) {
                console.error(err);
            }
        },

        removeAnnotation(index) {
            this.$bus.$emit('pdf::annotation::remove', index);
        },

        hideContextMenu() {
            $('[id^="pdf-context-menu-"]').hide();
        },
        showContextMenu(e) {
            if (!this.isAddingSignature && !this.isAnnotating) return;
            this.$bus.$emit("pdf::contextmenu::clickedarea", e);
            this.clickedArea = e;
            let menu = document.getElementById("pdf-context-menu-" + this.pageNumber);
            let rect = e.target.getBoundingClientRect();
            menu.style.left = Math.floor(e.clientX - rect.left) + 'px'
            menu.style.top = Math.floor(e.clientY - rect.top) + 'px'
            menu.style.display = 'block'
        },

    }
}
</script>

<style lang="scss" scoped>
.page {
    position: relative;
}

.draggable.vdr {
    border: none
}

.draggable.vdr.active {
    border: dashed 1px;
    border-radius: 10px;
    border-bottom: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.draggable.vdr.active .maskActive-Signature:after {
    border: dashed 1px;
    border-radius: 10px;
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.annotations {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    border: 0px;
    z-index: 1;
    max-width: 100%;
    height: auto !important;
    margin: 0 auto;

    .wrapper {
        position: relative;
        height: 100%;
        width: 100%;
    }
}

.maskActive {
    &-Signature {
        border-radius: 10px;
        display: block;
        background-color: #f8a52b;
        position: absolute;
        top: 0%;
        left: 0%;
        width: 100%;
        height: 100%;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        z-index: 1;

        &:after {
            bottom: -25px;
            left: 0;
            right: 0;
            position: absolute;
            content: 'Full Name, Date: 01/01/2021';
            height: 25px;
            background-color: #ffbc5a;
            padding: 0 10px;
            line-height: 25px;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            color: #a56201;
        }

        .mask-button {
            border-radius: 50%;
            border: 0;
            height: 25px;
            width: 25px;
            line-height: 0;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0;
            top: 10px;
            position: absolute;
            right: 10px;

            &:hover {
                opacity: 1;
            }
        }

    }

    &-Annotation {
        .mask-button {
            opacity: 0;
            position: absolute;
            right: -25px;
            top: -25px;
            line-height: 0px;
            height: 30px;
            width: 30px;
            border-radius: 100%;
            background-color: #fff;
            color: #000;
        }

        &:hover {
            .mask-button {
                opacity: 1;
            }
        }

        textarea {
            font-weight: 600;
        }

        p {
            font-weight: 600;
        }
    }
}
</style>
<style>
.transparent-field {
    resize: none;
    border: none;
    width: auto;
    background-color: transparent;
    font-weight: 400;
    box-sizing: border-box;
}

.transparent-field:focus {
    outline: 0 !important;
    border: none !important;
    box-shadow: none !important;
}

.textLayer span::selection {
    color: transparent !important;
    background: blue !important;
}

.textLayer span::-moz-selection {
    color: transparent !important;
    background: blue !important;
}

.annotation .wrapper .textLayer {
    overflow: hidden;
    position: relative;
    opacity: 0.4 !important;
}

.annotations .wrapper .textLayer span {
    display: none !important;
}

.annotations.active .wrapper .textLayer > span {
    display: inline-block !important;
    color: transparent;
    position: absolute;
    line-height: 1.4;
    white-space: pre;
    cursor: text;
    opacity: 0.8;
    transform-origin: 0% 0%;
}

.annotations.active .wrapper .textLayer .highlight {
    margin: -1px;
    padding: 1px;

    background-color: rgba(180, 0, 170, 0.2);
    border-radius: 4px;
}

.annotations.active .wrapper .textLayer .highlight.begin {
    border-radius: 4px 0px 0px 4px;
}

.annotations.active .wrapper .textLayer .highlight.end {
    border-radius: 0px 4px 4px 0px;
}

.annotations.active .wrapper .textLayer .highlight.middle {
    border-radius: 0px;
}

.annotations.active .wrapper .textLayer .highlight.selected {
    background-color: rgba(0, 100, 0, 0.2);
}

</style>
